import {
  extractAlphaFromHex,
  getBackgroundStyleFromImageURL,
  getCloneId,
  isCustomFont
} from '../utils';
import { loadFonts } from '../utils/FontLoader';
import BackgroundStyle from './BackgroundStyle';
import { FrameData, TextFrameData } from './FlyerData';
import { Frame } from './Frame';
import { FrameType } from './FrameType';
import { getFontFromTextStyle, isSystemFont } from './IFont';
import { TextAlignment } from './TextAlignment';

const arabicReg = /[\u0600-\u06FF]/;
const isTextArabic = (text: string): boolean => {
  return arabicReg.test(text);
};

const hebrewReg = /[\u0590-\u05FF]/;
const isTextHebrew = (text: string): boolean => {
  return hebrewReg.test(text);
};

export class TextFrame extends Frame {
  type = FrameType.Text;
  isEditable = true;
  isResizable = true;

  textStyle: PIXI.TextStyle;
  textMetrics: PIXI.TextMetrics;

  initialFontSize: number;
  initialLetterSpacing: number;

  get canRotate(): boolean {
    return (
      this.textStyle.align === TextAlignment.Center &&
      ((this.textStyle as any).verticalAlign as TextAlignment) ===
        TextAlignment.Middle
    );
  }

  private _text = '';
  get text(): string {
    return this._text;
  }
  set text(value: string) {
    this._text = value;
    if (this.textSource) {
      if (isTextArabic(value) || isTextHebrew(value)) {
        this.textStyle.letterSpacing = 0;
      }
      this.textSource.text = value;
      if (value !== '') {
        this.textMetrics = PIXI.TextMetrics.measureText(value, this.textStyle!);
        this.alignText();
      }
    }
  }

  setOneLineText(value: string) {
    this._text = value;
    this.textStyle.fontSize = this.initialFontSize;
    const isArabic = isTextArabic(value);
    if (isArabic) {
      this.textStyle.letterSpacing = 0;
    } else {
      this.textStyle.letterSpacing = this.initialLetterSpacing;
    }
    this.textSource.text = value;
    this.textMetrics = PIXI.TextMetrics.measureText(value, this.textStyle);
    // reduce font size until there is only 1 line left
    let spacingRatio = 0;
    if (this.initialLetterSpacing !== 0 && !isArabic) {
      spacingRatio = this.initialLetterSpacing / this.initialFontSize;
    }
    while (
      (this.textMetrics.lines.length > 1 ||
        this.textMetrics.width > this._boundingBox.width) &&
      this.textStyle.fontSize > 1
    ) {
      this.textStyle.fontSize--;
      this.textStyle.letterSpacing = this.textStyle.fontSize * spacingRatio;
      this.textMetrics = PIXI.TextMetrics.measureText(value, this.textStyle);
    }
    this.alignText();
  }

  get color(): string {
    const { fill } = this.textStyle;
    return (Array.isArray(fill) ? fill[0] : fill) as string;
  }
  set color(hex: string) {
    this.textStyle.fill = hex;
    this.emit('update');
  }

  get stroke(): string {
    return this.textStyle.stroke as string;
  }
  set stroke(hex: string) {
    this.textStyle.stroke = hex;
    this.emit('update');
  }

  get dropShadowColor(): string {
    return this.textStyle.dropShadowColor as string;
  }
  set dropShadowColor(hex: string) {
    this.textStyle.dropShadowColor = hex;
    this.emit('update');
  }

  get boundingBox(): PIXI.Rectangle {
    return this._boundingBox;
  }
  set boundingBox(value: PIXI.Rectangle) {
    this._boundingBox = value;
    // console.log(
    //   `x:${value.x} y:${value.y} width:${value.width} height:${value.height}`
    // );
    if (value) {
      this.textStyle.wordWrapWidth = value.width;
      this.textMetrics = PIXI.TextMetrics.measureText(
        this._text,
        this.textStyle!
      );
      this.alignText();
    }
  }

  protected get textSource(): PIXI.Text {
    return this.sprite as PIXI.Text;
  }

  didChangeStyle(property: string) {
    this.textMetrics = PIXI.TextMetrics.measureText(this.text, this.textStyle!);
    this.alignText();
    this.emit('update');
  }

  alignText() {
    if (!this.textStyle || !this.textSource || !this.boundingBox) {
      return;
    }
    let anchorX = 0.5;
    switch (this.textStyle.align) {
      case TextAlignment.Left:
        anchorX = 0;
        this.textSource.x = this.boundingBox.x;
        this.rotation = 0;
        break;
      case TextAlignment.Right:
        anchorX = 1;
        this.textSource.x = this.boundingBox.right;
        this.rotation = 0;
        break;
      case TextAlignment.Center:
        this.textSource.x = this.boundingBox.x + this.boundingBox.width / 2;
        break;
      default:
        break;
    }
    switch ((this.textStyle as any).verticalAlign as TextAlignment) {
      case TextAlignment.Top:
        this.textSource.anchor.set(anchorX, 0);
        this.textSource.y = this.boundingBox.y;
        this.rotation = 0;
        break;
      case TextAlignment.Bottom:
        this.textSource.anchor.set(anchorX, 1);
        this.textSource.y = this.boundingBox.bottom;
        this.rotation = 0;
        break;
      case TextAlignment.Middle:
        this.textSource.anchor.set(anchorX, 0.5);
        this.textSource.y = this.boundingBox.y + this.boundingBox.height / 2;
        break;
      default:
        break;
    }
  }

  moveText(x: number, y: number) {
    if (!this.textSource) {
      return;
    }
    this.textSource.x += x;
    this.textSource.y += y;
  }

  getCaretRect(position: number): PIXI.Rectangle {
    if (!this._boundingBox || !this.textStyle || !this.textMetrics) {
      return new PIXI.Rectangle();
    }
    const { lineHeight, lineWidths } = this.textMetrics;
    const lines: string[] = this.textMetrics!.lines as any;
    if (lines.length === 1) {
      lines[0] = this.text;
    }
    let linePosition = position;
    let lineIndex = 0;
    for (const l of lines) {
      if (linePosition > l.length && lineIndex < lines.length - 1) {
        lineIndex++;
        linePosition -= l.length + 1;
      } else {
        break;
      }
    }
    // console.log(`Caret position: ${lineIndex}, ${linePosition}`);
    const line = lines[lineIndex];
    const lineWidth = lineWidths[lineIndex];
    let lineStartingX = this._boundingBox.x;
    switch (this.textStyle.align) {
      case 'center':
        lineStartingX =
          this._boundingBox.x + (this._boundingBox.width - lineWidth) / 2;
        break;
      case 'right':
        lineStartingX = this._boundingBox.right - lineWidth;
        break;
      default:
        break;
    }
    const x = lineStartingX + this.getLineWidth(line.substr(0, linePosition));
    const y = this.textSource!.y + lineIndex * lineHeight;
    const height = this.textStyle.fontSize as number;
    return new PIXI.Rectangle(x, y, 0, height);
  }

  getLineWidth(line: string): number {
    const metrics = PIXI.TextMetrics.measureText(line, this.textStyle);
    return metrics.maxLineWidth;
  }

  getCaretPositionAt(x: number, y: number): number {
    if (!this._boundingBox || !this.textStyle || !this.textMetrics) {
      console.error(
        'Bounding box, text style, and text metrics should be set when asking for caret position'
      );
      return 0;
    }
    const localY = y - this.textSource!.y;
    const lines: string[] = this.textMetrics!.lines as any;
    // console.log(`getCaretPosition: ${localX}, ${localY}`);
    const lineIndex = Math.min(
      Math.floor(Math.max(0, localY) / this.textMetrics.lineHeight),
      this.textMetrics.lines.length - 1
    );
    const line = lines[lineIndex];
    const lineWidth = this.textMetrics.lineWidths[lineIndex];
    let lineStartingX = this._boundingBox.x;
    switch (this.textStyle.align) {
      case 'center':
        lineStartingX += (this._boundingBox.width - lineWidth) / 2;
        break;
      case 'right':
        lineStartingX += this._boundingBox.width - lineWidth;
        break;
      default:
        break;
    }
    const positionX = x - lineStartingX;
    let linePosition = 0;
    let str = '';
    const chars = line.split('');
    for (const c of chars) {
      str += c;
      const newWidth = this.getLineWidth(str);
      if (newWidth < positionX) {
        linePosition++;
      } else {
        break;
      }
    }
    let result = 0;
    for (let i = 0; i < lineIndex; i++) {
      result += lines[i].length + 1;
    }
    result += linePosition;
    return result;
  }

  async load() {
    if (this.isLoaded) {
      return;
    }

    this.initialFontSize = this.textStyle.fontSize as number;
    this.initialLetterSpacing = this.textStyle.letterSpacing as number;

    const font = getFontFromTextStyle(this.textStyle);
    if (!isSystemFont(font) && !isCustomFont(font.family)) {
      await loadFonts([font]);
    }
    this.textSource.style = this.textStyle;
    this.textMetrics = PIXI.TextMetrics.measureText(this.text, this.textStyle);
    this.alignText();

    this.isLoaded = true;
    (this.textSource as any).updateText();
    this.generateThumbnail();
    this.emit('load');
  }

  didUnselect() {
    this.updateThumbnail();
  }

  generateThumbnail() {
    this.generateThumbnailFromSource(
      this.textSource.texture.baseTexture.source as any
    );
  }

  updateThumbnail() {
    this.generateThumbnail();
    getBackgroundStyleFromImageURL(this.thumbnailURL).then(
      (style: BackgroundStyle) => {
        this.backgroundStyle = style;
        this.emit('edited');
      }
    );
  }

  populateWithData(data: FrameData) {
    const textData = data as TextFrameData;
    const styleOptions: PIXI.TextStyleOptions = {
      fontFamily: textData.family,
      fontStyle: textData.style || 'normal',
      fontWeight: textData.weight + '',
      fontSize: textData.size,
      lineHeight: textData.lineHeight,
      letterSpacing: textData.letterSpacing,
      fill: textData.color,
      wordWrap: true,
      wordWrapWidth: textData.box.width,
      fillGradientType: textData.fillGradientType || 0,
      align: textData.alignment[0],
      padding: 100
    };
    if (textData.dropShadowColor) {
      styleOptions.dropShadow = true;
      const components = extractAlphaFromHex(textData.dropShadowColor);
      styleOptions.dropShadowColor = components[0];
      styleOptions.dropShadowAlpha = components[1];
      styleOptions.dropShadowAngle = textData.dropShadowAngle;
      styleOptions.dropShadowBlur = textData.dropShadowBlur;
      styleOptions.dropShadowDistance = textData.dropShadowDistance;
    }
    if (textData.strokeThickness) {
      styleOptions.stroke = textData.stroke;
      styleOptions.strokeThickness = textData.strokeThickness;
    }
    this.textStyle = new PIXI.TextStyle(styleOptions) as PIXI.TextStyle;
    (this.textStyle as any).verticalAlign = textData.alignment[1];
    if (textData.textTransform) {
      (this.textStyle as any).textTransform = textData.textTransform;
    }
    this.sprite = new PIXI.Text(textData.text, this.textStyle);
    const { x, y, width, height } = textData.box;
    this._boundingBox = new PIXI.Rectangle(x, y, width, height);
    this.text = textData.text;
    if (textData.rotation) {
      this.rotation = textData.rotation;
    }
  }

  asData(forDatabase: boolean): FrameData {
    const { text } = this;
    const {
      align,
      fontFamily,
      fontStyle,
      fontWeight,
      fill,
      fontSize,
      letterSpacing,
      lineHeight,
      fillGradientType
    } = this.textStyle;
    const { x, y, width, height } = this.boundingBox;
    const result: TextFrameData = {
      ...super.asData(forDatabase),
      text,
      family: fontFamily as string,
      weight: parseInt(fontWeight, 10),
      color: fill as string | string[],
      size: fontSize as number,
      alignment: [align, (this.textStyle as any).verticalAlign],
      lineHeight,
      letterSpacing,
      box: {
        x,
        y,
        width,
        height
      },
      fillGradientType
    };

    if (fontStyle !== 'normal') {
      result.style = fontStyle;
    }

    if (this.textStyle.dropShadow) {
      const {
        dropShadowAlpha,
        dropShadowAngle,
        dropShadowBlur,
        dropShadowColor,
        dropShadowDistance
      } = this.textStyle;
      result.dropShadowColor = dropShadowColor as string;
      if (dropShadowAlpha < 1) {
        result.dropShadowColor += Math.round(dropShadowAlpha * 255).toString(
          16
        );
      }
      result.dropShadowAngle = dropShadowAngle;
      result.dropShadowBlur = dropShadowBlur;
      result.dropShadowDistance = dropShadowDistance;
    }

    if (this.textStyle.strokeThickness) {
      result.stroke = this.textStyle.stroke as string;
      result.strokeThickness = this.textStyle.strokeThickness;
    }

    if ((this.textStyle as any).textTransform) {
      result.textTransform = (this.textStyle as any).textTransform;
    }

    return result;
  }

  clone(copyIndex: number): Frame {
    const clone = new TextFrame(this.stageWidth, this.stageHeight);
    clone.id = getCloneId(this.id, copyIndex);
    clone.populateWithData(this.asData(false));
    clone.backgroundStyle = this.backgroundStyle;
    return clone;
  }
}



// WEBPACK FOOTER //
// ./src/data/TextFrame.ts